import { useNavigation } from '@remix-run/react'
import { useCallback } from 'react'
import type { PriceCriteria } from '~/features/search/criteria'
import { SCENE_TAGS } from '~/lib/constants/constants'
import type { RestaurantTagId } from '~/lib/graphql/scalars'
import { useReserveNavigate, useReserveOverlayContext } from '~/navigation'
import { createChildUses, createSeatTypes } from './criteria'
import type { ChildUseCriteria, FreeFlows, SeatCriteria, SmokingAllowedCheckType } from './types'

export function useSetBuffet() {
  const navigate = useReserveNavigate()
  return useCallback(
    (v: boolean) => {
      navigate({
        type: 'updateFilterCriteria',
        buffet: v,
      })
    },
    [navigate]
  )
}

export function useSetSmokingAllowed() {
  const navigate = useReserveNavigate()
  return useCallback(
    (v: SmokingAllowedCheckType) => {
      navigate({
        type: 'updateFilterCriteria',
        smokingAllowed: v === 'allow' ? true : v === 'notAllow' ? false : undefined,
      })
    },
    [navigate]
  )
}

export function useSetSeatAttributes() {
  const navigate = useReserveNavigate()
  return useCallback(
    (v: SeatCriteria) => {
      const { windowSide } = v
      const seatTypes = createSeatTypes({
        privateRoom: v.privateRoom,
        semiPrivateRoom: v.semiPrivateRoom,
        terrace: v.terrace,
        counter: v.counter,
        table: v.table,
      })
      navigate({
        type: 'updateFilterCriteria',
        seatTypes,
        windowSide,
      })
    },
    [navigate]
  )
}

export function useSetChildUse() {
  const navigate = useReserveNavigate()
  return useCallback(
    (v: ChildUseCriteria) => {
      const childUses = createChildUses({
        gradeSchoolers: v.gradeSchoolers,
        preSchoolers: v.preSchoolers,
      })
      navigate({
        type: 'updateFilterCriteria',
        childUses,
      })
    },
    [navigate]
  )
}

export function useClear() {
  const navigate = useReserveNavigate()
  return useCallback(() => {
    navigate({
      type: 'updateFilterCriteria',
      buffet: undefined,
      freeFlow: undefined,
      sceneTagIds: undefined,
      seatTypes: undefined,
      smokingAllowed: undefined,
      windowSide: undefined,
      childUses: undefined,
      lowerBoundPrice: undefined,
      upperBoundPrice: undefined,
    })
  }, [navigate])
}

export function useBaseSetFreeFlows(state: 'GUIDE' | 'COURSE_SELECT') {
  const ctx = useReserveOverlayContext(state)
  const navigate = useReserveNavigate()
  return useCallback(
    (v: FreeFlows) => {
      const sceneTagIds = ctx?.sceneTagIds ?? []
      navigate({
        type: 'updateFilterCriteria',
        freeFlow: v.freeFlow,
        sceneTagIds: v.freeFlowThreeHours
          ? [...sceneTagIds, SCENE_TAGS.FREE_FLOW_THREE_HOURS]
          : sceneTagIds.filter((id) => id !== SCENE_TAGS.FREE_FLOW_THREE_HOURS),
      })
    },
    [ctx, navigate]
  )
}

export function useBaseSetAfternoonTea(state: 'GUIDE' | 'COURSE_SELECT') {
  const ctx = useReserveOverlayContext(state)
  const navigate = useReserveNavigate()
  return useCallback(
    (v: boolean) => {
      const sceneTagIds = ctx?.sceneTagIds ?? []
      navigate({
        type: 'updateFilterCriteria',
        sceneTagIds: v
          ? [...sceneTagIds, '2981' as RestaurantTagId]
          : sceneTagIds.filter((id) => id !== '2981'),
      })
    },
    [ctx, navigate]
  )
}

export function useBaseSetChristmas(state: 'GUIDE' | 'COURSE_SELECT') {
  const ctx = useReserveOverlayContext(state)
  const navigate = useReserveNavigate()
  return useCallback(
    (v: boolean) => {
      const sceneTagIds = ctx?.sceneTagIds ?? []
      navigate({
        type: 'updateFilterCriteria',
        sceneTagIds: v
          ? [...sceneTagIds, SCENE_TAGS.CHRISTMAS]
          : sceneTagIds.filter((id) => id !== SCENE_TAGS.CHRISTMAS),
      })
    },
    [ctx, navigate]
  )
}

export function useBaseSetApplyPriceRange() {
  const navigate = useReserveNavigate()
  return useCallback(
    (v: PriceCriteria) => {
      navigate({
        type: 'updateFilterCriteria',
        lowerBoundPrice: v.lowerBoundPrice,
        upperBoundPrice: v.upperBoundPrice,
      })
    },
    [navigate]
  )
}

export function useIsLoading(state: 'GUIDE' | 'COURSE_SELECT'): boolean {
  const { state: navigationState, location } = useNavigation()
  // navigation state の loading のみで判定すると、ガイドから他のモーダルやふすまに遷移する際もローディングと判定されてしまうため、
  // 現在の state と遷移先の state が一致している場合のみローディングと判定するようにする
  return location?.state?.stack?.at(-1)?.value === state && navigationState === 'loading'
}
